import { useNavigate } from 'react-router-dom';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Paper,
  Text,
  Title,
  PageHeader,
  Group,
  VIPSO_RED,
} from '@vipso/shared';
import {
  IconUsers,
  IconBuildingStore,
  IconUserCheck,
  IconBuildingWarehouse,
  IconMapPin,
  IconTruck,
  IconHammer,
  IconSettings,
  IconChevronRight,
  IconShieldHalfFilled,
  IconHierarchy2,
} from '@tabler/icons-react';

type MoreTile = {
  to: string;
  labelKey: string;
  icon: ComponentType<{ size?: number | string; stroke?: number; color?: string }>;
};

const MORE_TILES: MoreTile[] = [
  { to: '/customers', labelKey: 'nav.customers', icon: IconUsers },
  { to: '/vendors', labelKey: 'nav.vendors', icon: IconBuildingStore },
  { to: '/staff', labelKey: 'nav.staff', icon: IconUserCheck },
  { to: '/roles', labelKey: 'nav.roles', icon: IconShieldHalfFilled },
  { to: '/approval-flows', labelKey: 'nav.approvalFlows', icon: IconHierarchy2 },
  { to: '/warehouse', labelKey: 'nav.warehouse', icon: IconBuildingWarehouse },
  { to: '/storage', labelKey: 'nav.storage', icon: IconMapPin },
  { to: '/transport', labelKey: 'nav.transport', icon: IconTruck },
  { to: '/labour', labelKey: 'nav.labour', icon: IconHammer },
];

export function MobileMorePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('nav.more')}
        description={t('nav.moreDescription')}
      />

      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        {MORE_TILES.map((tile) => {
          const IconCmp = tile.icon;
          return (
            <Box
              key={tile.to}
              component="button"
              type="button"
              onClick={() => navigate(tile.to)}
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 14,
                background: '#fff',
                padding: 18,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                textAlign: 'center',
                minHeight: 120,
                boxShadow: '0 2px 10px rgba(0,14,33,0.04)',
              }}
            >
              <Box
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'var(--mantine-color-brand-0)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <IconCmp size={24} stroke={1.75} color={VIPSO_RED} />
              </Box>
              <Text size="sm" fw={600} c="navy.9">
                {t(tile.labelKey)}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Paper
        component="button"
        type="button"
        onClick={() => navigate('/settings')}
        p="md"
        radius="md"
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          background: '#fff',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--mantine-color-gray-1)',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <IconSettings size={20} stroke={1.75} color="var(--mantine-color-navy-8)" />
            </Box>
            <Stack gap={2}>
              <Title order={5} c="navy.9">
                {t('nav.settings')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('settings.shortDescription')}
              </Text>
            </Stack>
          </Group>
          <IconChevronRight size={18} stroke={1.75} color="var(--mantine-color-gray-5)" />
        </Group>
      </Paper>
    </Stack>
  );
}
